import type { Component, JSX } from "solid-js";
import { createEffect, createMemo, createSignal, For, getOwner, on, runWithOwner, Show, untrack } from "solid-js";
import type SubmissionId from "../../../state/SubmissionId";
import ApiService from "../../../services/ApiService";
import type FormId from "../../../state/FormId";
import { indexOf, isEmpty, isEqual, isNil } from "lodash-es";
import type { TranslationKey } from "../../../state/i18n";
import { useTranslation } from "../../../state/i18n";
import DetailModal from "../../../components/modal/DetailModal";
import { Icon } from "../../../components/icon/Icon";
import { TextAndIconButton } from "../../../components/button/Button";
import InlineAccordion from "../../../components/accordion/InlineAccordion";
import type { PillVariant } from "../../../components/pill/Pill";
import Pill from "../../../components/pill/Pill";
import type { HookPipeline } from "../../../types/HookPipeline";
import FileDownloadButton from "../../../fields/valueRendering/FileDownloadButton";
import type { MailStatuses } from "../../../types/Submission";
import { keyFromSelector } from "i18next";
import { IconTile } from "../../../components/icon/IconTile";
import GenericFailureModal from "../../GenericFailureModal";
import { useModalManager } from "../../../components/modal/ModalManager";
import { findMailTransitionsForFile } from "../../../utils/mailStatusUtil";

const MINIMUM_BUTTON_LOADING_DELAY = 350;

type HookPipelineModalProps = {
  formId: FormId;
  submissionId: SubmissionId;
  mailStatuses: MailStatuses[];
};

const getStatusVariantAndLabel = (
  status: string,
  pending: boolean,
  hasEmailWarnings: boolean,
): { variant: PillVariant; label: TranslationKey } => {
  if (pending) {
    return { variant: "neutral", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_PENDING) };
  }

  if (status === "SUCCESS" && hasEmailWarnings) {
    return { variant: "warning", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_WARNING) };
  }

  switch (status) {
    case "OPEN":
      return { variant: "neutral", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_OPEN) };
    case "SUCCESS":
      return { variant: "success", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_SUCCESS) };
    case "SKIPPED":
      return { variant: "neutral", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_SKIPPED) };
    case "IN_PROGRESS":
      return { variant: "informative", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_IN_PROGRESS) };
    case "RETRY":
      return { variant: "informative", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_RETRY) };
    case "ERROR":
      return { variant: "danger", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_FAILED) };
    case "BLOCKED":
      return { variant: "danger", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_BLOCKED) };
    default:
      return { variant: "warning", label: keyFromSelector(($) => $.HOOK_PIPELINE_STEP_STATUS_OUT_OF_CREDITS) };
  }
};

const HookPipelineModal: Component<HookPipelineModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();

  const [pendingStep, setPendingStep] = createSignal<number>(-1);
  const [hasFailedStep, setHasFailedStep] = createSignal<boolean>(false);

  const [isRerunningAllSteps, setIsRerunningAllSteps] = createSignal<boolean>(false);
  const [isRerunningFromFailedStep, setIsRerunningFromFailedStep] = createSignal<boolean>(false);

  const hookPipelineQuery = ApiService.getHookPipeline(
    untrack(() => props.formId),
    untrack(() => props.submissionId),
  );

  const [displayData, setDisplayData] = createSignal<HookPipeline>();
  const hasNoHookPipelineSteps = createMemo(() => isEmpty(displayData()?.steps));

  const owner = getOwner();

  const skipStep = () => {
    runWithOwner(owner, () => {
      const mutation = ApiService.skipHookpipelineStep("skip");
      mutation.mutate({ formId: untrack(() => props.formId), submissionId: untrack(() => props.submissionId) });
    });
  };

  const retryStep = () => {
    if (isNil(hookPipelineQuery?.data)) {
      return;
    }

    const statuses = hookPipelineQuery.data.steps.map((v) => v.status);
    setPendingStep(indexOf(statuses, "ERROR"));

    setHasFailedStep(false);
    runWithOwner(owner, () => {
      setIsRerunningFromFailedStep(true);
      const mutation = ApiService.skipHookpipelineStep("retry");
      mutation.mutate(
        { formId: untrack(() => props.formId), submissionId: untrack(() => props.submissionId) },
        {
          onSettled: () => {
            setTimeout(() => setIsRerunningFromFailedStep(false), MINIMUM_BUTTON_LOADING_DELAY);
          },
        },
      );
    });
  };

  const rerunAllSteps = () => {
    setDisplayData((prev) => {
      if (isNil(prev)) {
        return prev;
      }
      return { ...prev, steps: prev.steps.map((step) => ({ ...step, status: "OPEN" })) };
    });
    runWithOwner(owner, () => {
      setIsRerunningAllSteps(true);
      const mutation = ApiService.resendSubmission();
      mutation.mutate(
        untrack(() => props.submissionId),
        {
          onSettled: () => {
            setTimeout(() => setIsRerunningAllSteps(false), MINIMUM_BUTTON_LOADING_DELAY);
          },
        },
      );
    });
  };

  createEffect(
    on(
      () => hookPipelineQuery.data,
      (data) => {
        if (isNil(data) || isEqual(data, untrack(displayData))) {
          return;
        }
        setDisplayData(data);

        const statuses = data.steps.map((step) => step.status);
        setHasFailedStep(statuses.includes("ERROR"));
        setPendingStep(indexOf(statuses, "OPEN"));
      },
    ),
  );

  createEffect(
    on(
      () => hookPipelineQuery.error,
      (error) => {
        if (isNil(error)) {
          return;
        }
        replace({
          content: () => <GenericFailureModal title={t(($) => $.HOOK_PIPELINE_ERROR_MODAL_TITLE)} error={error} />,
        });
      },
    ),
  );

  return (
    <DetailModal
      title={t(($) => $.HOOK_PIPELINE_MODAL_TITLE)}
      description={t(($) => $.HOOK_PIPELINE_MODAL_DESCRIPTION)}
      content={() => (
        <Show
          when={displayData()}
          // Keyed is used to preserve the scroll position after a api refresh
          keyed
          fallback={
            <div class="flex justify-center py-4">
              <Icon name="spinner" size="xl" type="solid" class="animate-spin" />
            </div>
          }
        >
          {(data) => (
            <table class="w-full table-fixed text-left">
              <thead>
                <tr class="border-b border-subtle text-base-semibold text-secondary">
                  <th class="w-75 py-1.5">{t(($) => $.HOOK_PIPELINE_MODAL_STEP_COLUMN_TITLE)}</th>
                  <th class="py-2">{t(($) => $.HOOK_PIPELINE_MODAL_STATUS_COLUMN_TITLE)}</th>
                  <th class="py-2">{t(($) => $.HOOK_PIPELINE_MODAL_ACTION_COLUMN_TITLE)}</th>
                </tr>
              </thead>
              <tbody>
                <Show
                  when={!isEmpty(data.steps ?? [])}
                  fallback={
                    <tr class="border-t border-subtle align-top text-medium-regular text-secondary">
                      <td class="py-3" colspan="4">
                        {t(($) => $.HOOK_PIPELINE_STEP_EMPTY_LABEL)}
                      </td>
                    </tr>
                  }
                >
                  <For each={data.steps}>
                    {(step, index) => {
                      let recipients: JSX.Element[] = step?.response?.data?.recipients ?? [];
                      let hasEmailWarnings = false;

                      if (step?.configuration?.key === "registrationprocessor") {
                        const fileIds = step?.response?.files?.map(({ fileId }) => fileId) ?? [];

                        const mailStatuses = fileIds.flatMap((fileId) =>
                          findMailTransitionsForFile(fileId, props.mailStatuses),
                        );

                        hasEmailWarnings = mailStatuses.some(
                          ({ status }) => status === "reject" || status === "hard_bounce",
                        );

                        recipients = mailStatuses.map((recipient) => (
                          <div>
                            {recipient.email}
                            <Show when={recipient.bounceDescription}>
                              <div class="mr-1 text-status-warning">
                                <Icon
                                  name="exclamation"
                                  type="solid"
                                  size="sm"
                                  class="mr-1 inline icon-status-warning"
                                />
                                {recipient.bounceDescription}
                              </div>
                            </Show>
                          </div>
                        ));
                      }

                      const status = createMemo(() =>
                        getStatusVariantAndLabel(
                          step.status,
                          index() === pendingStep() && !hasFailedStep(),
                          hasEmailWarnings,
                        ),
                      );

                      const errorMessage: string | undefined = step?.response?.message ?? step?.response?.exception;

                      return (
                        <tr class="border-t border-subtle align-top">
                          <td class="flex gap-4 py-3">
                            <IconTile icon={step.icon} size="md" border />
                            <div
                              classList={{
                                "pt-2.5": isEmpty(recipients) && isNil(errorMessage),
                                "pt-0.5": !isEmpty(recipients) || !isNil(errorMessage),
                              }}
                            >
                              <div class="mb-0.5 w-full truncate text-base-medium text-primary">
                                {(step.configuration?.configuration["name"] as string) ?? t(step.titleKey)}
                              </div>

                              <Show when={!isEmpty(recipients)}>
                                <InlineAccordion
                                  {...(hasEmailWarnings ? { variant: "warning", icon: "exclamation" } : {})}
                                  expandLabel={`${t(($) => $.HOOK_PIPELINE_STEP_RECIPIENTS_TITLE)} (${recipients.length})`}
                                  collapseLabel={`${t(($) => $.HOOK_PIPELINE_STEP_RECIPIENTS_TITLE)} (${recipients.length})`}
                                >
                                  <div class="mt-1 flex flex-col text-small-regular text-secondary">
                                    <For each={recipients}>
                                      {(recipient) => <div class="wrap-break-word">{recipient}</div>}
                                    </For>
                                  </div>
                                </InlineAccordion>
                              </Show>

                              <Show when={!isNil(errorMessage)}>
                                <InlineAccordion
                                  expandLabel={t(($) => $.HOOK_PIPELINE_STEP_SHOW_ERROR_TITLE)}
                                  collapseLabel={t(($) => $.HOOK_PIPELINE_STEP_HIDE_ERROR_TITLE)}
                                  variant="error"
                                  icon="exclamationCircle"
                                >
                                  <div class="mt-1 text-small-regular text-status-error">{errorMessage}</div>
                                </InlineAccordion>
                              </Show>
                            </div>
                          </td>
                          <td class="pt-4.5 pb-3">
                            <Pill variant={status().variant} label={t(status().label)} />
                          </td>
                          <td class="py-4.5">
                            <For each={step.response?.files ?? []}>
                              {(file) => <FileDownloadButton fileId={file.fileId} />}
                            </For>
                            <Show when={["SUCCESS", "OPEN", "SKIPPED"].indexOf(step.status) === -1}>
                              <TextAndIconButton
                                size="medium"
                                icon="fastForward"
                                variant="secondary"
                                text={t(($) => $.HOOK_PIPELINE_STEP_SKIP_LABEL)}
                                onClick={() => {
                                  skipStep();
                                }}
                              />
                            </Show>
                          </td>
                        </tr>
                      );
                    }}
                  </For>
                </Show>
              </tbody>
            </table>
          )}
        </Show>
      )}
      footer={
        <div class="flex justify-end gap-2">
          <TextAndIconButton
            icon="refresh"
            text={t(($) => $.HOOK_PIPELINE_MODAL_RERUN_FROM_FAILED_TITLE)}
            variant="secondary"
            loading={isRerunningFromFailedStep()}
            disabled={!hasFailedStep() || hasNoHookPipelineSteps()}
            onClick={() => retryStep()}
            size="medium"
          />
          <TextAndIconButton
            icon="refresh"
            text={t(($) => $.HOOK_PIPELINE_MODAL_RERUN_ALL_TITLE)}
            variant="secondary"
            loading={isRerunningAllSteps()}
            disabled={(pendingStep() !== -1 && !hasFailedStep()) || hasNoHookPipelineSteps()}
            onClick={() => rerunAllSteps()}
            size="medium"
          />
        </div>
      }
      fullHeight
    />
  );
};

export default HookPipelineModal;
